import { supabaseClient } from '../supabase';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../logger';
import type { TranslationContext } from './types';
import { translationCache } from './translationCache';
import { analyticsService } from '../analytics/analyticsService';

class AutoTranslator {
  private static instance: AutoTranslator;
  private translationLocks: Map<string, Promise<string>> = new Map();
  private activeApiTranslations: Set<string> = new Set();
  private retryCount: number = 0;
  private maxRetries: number = 3;
  private retryDelay: number = 1000;

  private constructor() {}

  public static getInstance(): AutoTranslator {
    if (!AutoTranslator.instance) {
      AutoTranslator.instance = new AutoTranslator();
    }
    return AutoTranslator.instance;
  }

  private async generateHash(text: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private isEmptyText(text: string | undefined | null): boolean {
    if (!text) return true;
    const trimmed = text.trim();
    return trimmed === '' || trimmed === '\n';
  }

  private getLockKey(context: TranslationContext, description: string, targetLang: SupportedLanguages): string {
    switch (context.sectionType) {
      case 'event-api':
        return `event-api:${context.source_hash || description}:${targetLang}:${context.contentType || 'description'}`;
      case 'event':
        return `event:${context.idShow || context.show_id}:${targetLang}:${context.contentType || 'description'}`;
      case 'standard':
        return `standard:${context.itemId}:${targetLang}:${context.contentType || 'description'}`;
      case 'liste':
        return `liste:${context.itemId}:${targetLang}:${context.contentType || 'description'}`;
      default:
        logger.error('Invalid section type', {
          category: 'Translation',
          data: { context }
        });
        throw new Error(`Invalid section type: ${context.sectionType}`);
    }
  }

  public async translateDescription(
    description: string,
    targetLang: SupportedLanguages,
    context: TranslationContext
  ): Promise<string> {
    // Si le texte est vide, retourner directement
    if (!description || this.isEmptyText(description)) return description;

    // Si on est dans la langue par défaut, retourner directement la description
    if (targetLang === 'fr') {
      return description;
    }

    if (!context.itemId) {
      logger.error('Missing itemId for translation', {
        category: 'Translation',
        data: { context }
      });
      throw new Error('itemId is required for translation');
    }

    if (!context.sectionType) {
      logger.error('Missing sectionType for translation', {
        category: 'Translation',
        data: { context }
      });
      throw new Error('sectionType is required for translation');
    }

    // For event section, handle both idShow and show_id
    if (context.sectionType === 'event') {
      context.idShow = context.idShow || context.show_id;

      if (!context.idShow) {
        logger.warn('Missing idShow/show_id for event translation', {
          category: 'Translation',
          data: { context }
        });
      }
    }

    // Ensure contentType is set, default to 'description'
    context.contentType = context.contentType || 'description';

    const lockKey = this.getLockKey(context, description, targetLang);

    // Vérifier d'abord le cache
    const cached = translationCache.get(description, targetLang);
    if (cached) {
      return cached.text;
    }

    // Check if there's already a translation in progress
    if (this.translationLocks.has(lockKey)) {
      return this.translationLocks.get(lockKey)!;
    }

    // Create a new translation promise
    const translationPromise = (async () => {
      try {
        // Determine the unique identifier and query based on section type
        let uniqueIdentifier: string;
        let identifierField: string;
        let insertData: any = {
          fr: description,
          [targetLang]: '',
          sectiontype: context.sectionType,
          content_type: context.contentType
        };

        switch (context.sectionType) {
          case 'event-api':
            uniqueIdentifier = await this.generateHash(description);
            identifierField = 'source_hash';
            insertData.source_hash = uniqueIdentifier;
            break;
          case 'event':
            if (!context.idShow && !context.show_id) {
              throw new Error('idShow or show_id is required for event translations');
            }
            uniqueIdentifier = context.idShow || context.show_id || '';
            identifierField = 'idshow';
            insertData = {
              ...insertData,
              idshow: uniqueIdentifier,
              hotel_id: context.hotelId,
              section_id: context.tableId
            };
            break;
          case 'standard':
          case 'liste':
            uniqueIdentifier = context.itemId;
            identifierField = 'item_id';
            insertData = {
              ...insertData,
              item_id: uniqueIdentifier,
              hotel_id: context.hotelId,
              section_id: context.tableId
            };
            break;
          default:
            throw new Error(`Invalid section type: ${context.sectionType}`);
        }

        // Query using unique identifier and content_type
        const { data: records, error } = await supabaseClient
          .from('translations_section')
          .select('*')
          .eq(identifierField, uniqueIdentifier)
          .eq('content_type', context.contentType)
          .maybeSingle();

        if (error) {
          throw error;
        }

        // If translation exists
        if (records) {
          const translationField = targetLang;
          const existingTranslation = records[translationField];

          // If translation exists in this language
          if (existingTranslation && !this.isEmptyText(existingTranslation)) {
            // Cache the translation
            translationCache.set(description, targetLang, existingTranslation, 'db');

            return existingTranslation;
          }

          // If record exists but not the translation in this language
          // Mark this as an active API translation
          this.activeApiTranslations.add(lockKey);
          
          const translatedText = await this.translateWithGoogleAPI(description, targetLang);

          // Track automatic translation
          analyticsService.trackEvent({ event_type: 'user', event_name: 'auto_translation', event_data: { from_language: 'fr', to_language: targetLang, success: true } });

          // Update the translation field
          const { error: updateError } = await supabaseClient
            .from('translations_section')
            .update({
              [targetLang]: translatedText,
              last_used: new Date().toISOString()
            })
            .eq('id', records.id);

          if (updateError) {
            throw updateError;
          }

          // Cache the new translation
          translationCache.set(description, targetLang, translatedText, 'api');
          
          // Remove from active API translations
          this.activeApiTranslations.delete(lockKey);

          return translatedText;
        }

        // If no translation exists, translate and create new record
        // Mark this as an active API translation
        this.activeApiTranslations.add(lockKey);
        
        const translatedText = await this.translateWithGoogleAPI(description, targetLang);

        // Track automatic translation
        analyticsService.trackEvent({ event_type: 'user', event_name: 'auto_translation', event_data: { from_language: 'fr', to_language: targetLang, success: true } });

        // Create new translation record with all required fields
        insertData[targetLang] = translatedText;
        insertData.last_used = new Date().toISOString();
        insertData.created_at = new Date().toISOString();

        const { error: insertError } = await supabaseClient
          .from('translations_section')
          .insert([insertData]);

        if (insertError) {
          throw insertError;
        }

        // Cache the new translation
        translationCache.set(description, targetLang, translatedText, 'api');
        
        // Remove from active API translations
        this.activeApiTranslations.delete(lockKey);

        return translatedText;

      } catch (error) {
        logger.error('Translation error', {
          category: 'Translation',
          data: { 
            error,
            context,
            language: targetLang,
            sectionType: context.sectionType,
            contentType: context.contentType
          }
        });

        // Track failed translation
        analyticsService.trackEvent({ event_type: 'user', event_name: 'auto_translation', event_data: { from_language: 'fr', to_language: targetLang, success: false } });
        
        // Remove from active API translations
        this.activeApiTranslations.delete(lockKey);

        throw error;
      } finally {
        this.translationLocks.delete(lockKey);
      }
    })();

    this.translationLocks.set(lockKey, translationPromise);
    return translationPromise;
  }

  // Check if a translation is actively being processed via API
  public isActivelyTranslating(lockKey: string): boolean {
    return this.activeApiTranslations.has(lockKey);
  }

  private async translateWithGoogleAPI(
    text: string,
    targetLang: SupportedLanguages
  ): Promise<string> {
    const apiKey = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
    if (!apiKey) {
      throw new Error('Missing Google Translate API key');
    }

    const response = await fetch(
      `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: text,
          source: 'fr',
          target: targetLang,
          format: 'text'
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Google Translate API error', {
        category: 'Translation',
        data: {
          status: response.status,
          statusText: response.statusText,
          errorText
        }
      });
      throw new Error(`Translation API error: ${response.status}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.message);
    }

    const translatedText = data.data.translations[0].translatedText;

    return translatedText;
  }
}

export const autoTranslator = AutoTranslator.getInstance();